"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Lightbulb, Cog, Shield } from "lucide-react";
export default function OurServicesOverview() {
  const {
    ref: section1Ref,
    isVisible: section1Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section2Ref,
    isVisible: section2Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section3Ref,
    isVisible: section3Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const services = [{
    icon: Lightbulb,
    title: "Product Development Consulting",
    description: "Transform your innovative concepts into market-ready products with our expert guidance on advanced materials and cutting-edge technologies.",
    link: "/ProductDevelopmentConsultingPage"
  }, {
    icon: Cog,
    title: "White-Label Product Support",
    description: "End-to-end support from prototype engineering to manufacturing scale-up, ensuring rapid time-to-market and cost efficiency.",
    link: "/PrivateLabelProductSupportPage"
  }, {
    icon: Shield,
    title: "Technical Services",
    description: "Specialized intellectual property strategy, due diligence, and merger & acquisition assistance to protect and grow your business.",
    link: "/DueDiligenceSupportPage"
  }];
  return <section className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">Our Expertise</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted sm:text-lg">
            Comprehensive solutions to bring your innovations to market with confidence and speed.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <div ref={section1Ref} data-index={0} className={`transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${section1Visible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <Card className="card h-full border border-border shadow-soft transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-medium">
              <CardHeader className="pb-4">
                <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10">
                  <Lightbulb className="size-6 text-primary" />
                </div>
                <CardTitle className="text-xl font-semibold text-foreground">
                  {services[0].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm text-muted-foreground leading-relaxed">Transform your innovative concepts into market-ready products.</p>
              </CardContent>
              <CardFooter>
                <Link to={services[0].link}>
                  <Button variant="outline" className="group transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] hover:shadow-medium active:scale-100">
                    Learn More
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>

          <div ref={section2Ref} data-index={1} className={`transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-100 ${section2Visible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <Card className="card h-full border border-border shadow-soft transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-medium">
              <CardHeader className="pb-4">
                <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-secondary/10">
                  <Cog className="size-6 text-secondary" />
                </div>
                <CardTitle className="text-xl font-semibold text-foreground">
                  {services[1].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {services[1].description}
                </p>
              </CardContent>
              <CardFooter>
                <Link to={services[1].link}>
                  <Button variant="outline" className="group transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] hover:shadow-medium active:scale-100">
                    Learn More
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>

          <div ref={section3Ref} data-index={2} className={`transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-200 ${section3Visible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <Card className="card h-full border border-border shadow-soft transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-medium">
              <CardHeader className="pb-4">
                <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-accent/10">
                  <Shield className="size-6 text-accent" />
                </div>
                <CardTitle className="text-xl font-semibold text-foreground">
                  {services[2].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {services[2].description}
                </p>
              </CardContent>
              <CardFooter>
                <Link to={services[2].link}>
                  <Button variant="outline" className="group transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] hover:shadow-medium active:scale-100">
                    Learn More
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}